import React, { useMemo, forwardRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

import type { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useCountrySelectData } from 'todistukset-common/src/vtj/ui/country-select.util'
import { useAriaOptionsFunction } from 'todistukset-ui/src/vtj/components/order-wizard/country-select.util'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'

import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'

import {
  validateOrdererExtraIdentificationBirthPlace,
  validateOrdererExtraIdentificationBirthCountry,
  validateOrdererExtraIdentificationFinnishHetu,
  validateOrdererExtraIdentificationForeignPersonId,
  validateOrdererExtraIdentificationForeignPersonIdCountry,
  validateOrdererExtraIdentificationPreviousName,
} from 'todistukset-common/src/vtj/form-validation.util'
import {
  getValidationErrorFromValidator,
  normalizeWhitespace,
} from 'todistukset-ui/src/vtj/components/order-wizard/validation'

import {
  Block,
  Heading,
  Paragraph,
  Text,
  SingleSelect,
  Checkbox,
  Tooltip,
} from 'suomifi-ui-components'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  ParagraphWithNewlines,
  NonEditableField,
  StyledTextInput,
  TextInputs,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import { OrdererExtraIdentification } from 'todistukset-common/src/vtj/types/todistus'
import { CountryKoodistoCode } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import {
  FormContainer,
  CheckboxToggleableGroup,
  SideBySide,
} from 'todistukset-ui/src/vtj/components/order-wizard/PersonalInformationStep.sc'
import { formatDateFormatToUiDateFormat } from 'todistukset-common/src/vtj/util/date.util'
import { isVtjOteTodistus } from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'

const PersonalInformationStep: StepComponent = observer(
  forwardRef(function PersionalInformationStep(
    { titleId, certificateType },
    ref
  ) {
    if (!isVtjOteTodistus(certificateType)) {
      throw new Error(
        `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
      )
    }

    const { t } = useTranslation()
    const {
      forms: { [certificateType]: form },
      common: { loggedInUser, lang },
      steps: {
        [certificateType]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const user = loggedInUser.get()

    const { countrySelectList, countrySelectIndex } = useCountrySelectData({
      lang,
      excludeCountries: [EuCountries.FINLAND],
    })
    const ariaOptionsFunction = useAriaOptionsFunction()

    const onChange: Record<
      keyof OrdererExtraIdentification,
      (v: string | number | undefined) => void
    > = useMemo(() => {
      const {
        ordererExtraIdentification: {
          setBirthPlace,
          setBirthCountry,
          setFinnishHetu,
          setForeignPersonId,
          setForeignPersonIdCountry,
          setPreviousName,
        },
      } = getFormActions(certificateType)
      return {
        birthPlace: setBirthPlace,
        birthCountry: setBirthCountry,
        finnishHetu: setFinnishHetu,

        foreignPersonId: setForeignPersonId,
        foreignPersonIdCountry: (value) =>
          setForeignPersonIdCountry(value as CountryKoodistoCode | null),

        previousName: setPreviousName,
      }
    }, [certificateType])

    const onChangeCheckbox: Record<
      keyof TodistusStoreForm['ordererExtraIdentificationOptions'],
      (v: boolean) => void
    > = useMemo(() => {
      const {
        ordererExtraIdentification: {
          setAddForeignPersonId,
          setAddPreviousName,
        },
      } = getFormActions(certificateType)
      return {
        addForeignPersonId: setAddForeignPersonId,
        addPreviousName: setAddPreviousName,
      }
    }, [certificateType])

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'birthPlace',
      'birthCountry',
      'finnishHetu',
      'foreignPersonIdCountry',
      'foreignPersonId',
      'previousName',
    ])

    function textInputProps(
      idName: string,
      fieldName: keyof OrdererExtraIdentification
    ) {
      return {
        id: idName,
        name: idName,
        type: 'text' as const,
        fullWidth: true,
        statusTextAriaLiveMode: 'polite' as const,
        value: form.ordererExtraIdentification?.[fieldName] ?? '',
        onChange: onChange[fieldName],
        forwardedRef: refs[fieldName],
        ...errorStatus({
          field: fieldName,
          form,
          showErrors,
          t,
        }),
      }
    }

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardPersonalInformationStepMainTitle" />
          </Heading>
          <ParagraphWithNewlines>
            <LocalizedString stringKey="orderWizardPersonalInformationStepMainDescription" />
          </ParagraphWithNewlines>
        </Block>

        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardPersonalInformationStepFormTitle" />
          </Heading>
          <ParagraphWithNewlines>
            <LocalizedString stringKey="orderWizardPersonalInformationStepFormDescription" />
          </ParagraphWithNewlines>

          <TextInputs>
            <NonEditableField>
              <Paragraph>
                <Text variant="bold">
                  <LocalizedString stringKey="orderWizardPersonalInformationStepInputLabel_name" />
                </Text>
              </Paragraph>
              <Paragraph>
                {user.lastName}, {user.firstNames}
              </Paragraph>
            </NonEditableField>
            <SideBySide>
              <NonEditableField>
                <Paragraph>
                  <Text variant="bold">
                    <LocalizedString stringKey="orderWizardPersonalInformationStepInputLabel_birthDate" />
                  </Text>
                </Paragraph>
                <Paragraph>
                  {user.birthday
                    ? formatDateFormatToUiDateFormat(user.birthday)
                    : '-'}
                </Paragraph>
              </NonEditableField>
              <StyledTextInput
                {...textInputProps('place-of-birth', 'birthPlace')}
                labelText={t(
                  'orderWizardPersonalInformationStepInputLabel_birthPlace'
                )}
              />
            </SideBySide>

            <StyledTextInput
              {...textInputProps('country-of-birth', 'birthCountry')}
              labelText={t(
                'orderWizardPersonalInformationStepInputLabel_birthCountry'
              )}
            />

            <StyledTextInput
              {...textInputProps('finnish-hetu', 'finnishHetu')}
              labelText={t(
                'orderWizardPersonalInformationStepInputLabel_finnishHetu'
              )}
              optionalText={t('orderWizardPersonalInformationStepIfKnown')}
            />

            <CheckboxToggleableGroup>
              <Checkbox
                id="add-foreign-person-id"
                checked={
                  form.ordererExtraIdentificationOptions.addForeignPersonId
                }
                onClick={() =>
                  onChangeCheckbox.addForeignPersonId(
                    !form.ordererExtraIdentificationOptions.addForeignPersonId
                  )
                }
              >
                <Text>
                  <LocalizedString stringKey="orderWizardPersonalInformationStepInputLabel_addForeignPersonId" />
                </Text>
              </Checkbox>

              {form.ordererExtraIdentificationOptions.addForeignPersonId && (
                <SingleSelect
                  key={`countrySelect-${lang}`} // fixed in https://github.com/vrk-kpa/suomifi-ui-components/pull/773
                  id="select-country-widget"
                  data-test-id="single-select-country"
                  labelText={t(
                    'orderWizardPersonalInformationStepInputLabel_foreignPersonIdCountry'
                  )}
                  tooltipComponent={<TooltipWhichCountry />}
                  visualPlaceholder={t(
                    'orderWizardUsageRegionStepCountrySelectPrompt'
                  )}
                  items={countrySelectList}
                  clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
                  noItemsText={
                    t('orderWizardSingleSelectNoMatchingItems') ?? ''
                  }
                  ariaOptionsAvailableTextFunction={ariaOptionsFunction}
                  selectedItem={
                    form.ordererExtraIdentification?.foreignPersonIdCountry
                      ? countrySelectIndex[
                          form.ordererExtraIdentification.foreignPersonIdCountry
                        ]
                      : null
                  }
                  onItemSelectionChange={(item) => {
                    const { uniqueItemId } = item ?? { uniqueItemId: undefined }
                    onChange.foreignPersonIdCountry(uniqueItemId)
                  }}
                  {...errorStatus({
                    field: 'foreignPersonIdCountry',
                    form,
                    showErrors,
                    t,
                  })}
                  forwardedRef={refs.foreignPersonIdCountry}
                />
              )}

              {form.ordererExtraIdentificationOptions.addForeignPersonId && (
                <StyledTextInput
                  {...textInputProps('foreign-person-id', 'foreignPersonId')}
                  labelText={t(
                    'orderWizardPersonalInformationStepInputLabel_foreignPersonId'
                  )}
                />
              )}
            </CheckboxToggleableGroup>

            <CheckboxToggleableGroup>
              <Checkbox
                checked={form.ordererExtraIdentificationOptions.addPreviousName}
                onClick={() =>
                  onChangeCheckbox.addPreviousName(
                    !form.ordererExtraIdentificationOptions.addPreviousName
                  )
                }
              >
                <Text>
                  <LocalizedString stringKey="orderWizardPersonalInformationStepInputLabel_addPreviousName" />
                </Text>
              </Checkbox>
            </CheckboxToggleableGroup>

            {form.ordererExtraIdentificationOptions.addPreviousName && (
              <StyledTextInput
                {...textInputProps('previous-name', 'previousName')}
                labelText={t(
                  'orderWizardPersonalInformationStepInputLabel_previousName'
                )}
              />
            )}
          </TextInputs>
        </Block>
      </FormContainer>
    )
  })
)

export default PersonalInformationStep

const errorStatus = (params: {
  field: keyof ReturnType<typeof getErrors>
  form: TodistusStoreForm
  showErrors: boolean
  t: TFunction
}) => getErrorStatus({ ...params, errors: getErrors(params.form) })

function getErrors(form: TodistusStoreForm) {
  if (!form.ordererExtraIdentification) {
    throw new Error('form.ordererExtraIdentification not initialized')
  }
  return {
    birthPlace: getValidationErrorFromValidator(
      normalizeWhitespace(validateOrdererExtraIdentificationBirthPlace),
      form.ordererExtraIdentification.birthPlace,
      form
    ),
    birthCountry: getValidationErrorFromValidator(
      normalizeWhitespace(validateOrdererExtraIdentificationBirthCountry),
      form.ordererExtraIdentification.birthCountry,
      form
    ),
    finnishHetu: getValidationErrorFromValidator(
      normalizeWhitespace(validateOrdererExtraIdentificationFinnishHetu),
      form.ordererExtraIdentification.finnishHetu,
      form
    ),
    foreignPersonIdCountry: getValidationErrorFromValidator(
      validateOrdererExtraIdentificationForeignPersonIdCountry,
      form.ordererExtraIdentification.foreignPersonIdCountry,
      form
    ),
    foreignPersonId: getValidationErrorFromValidator(
      normalizeWhitespace(validateOrdererExtraIdentificationForeignPersonId),
      form.ordererExtraIdentification.foreignPersonId,
      form
    ),
    previousName: getValidationErrorFromValidator(
      normalizeWhitespace(validateOrdererExtraIdentificationPreviousName),
      form.ordererExtraIdentification.previousName,
      form
    ),
  }
}

export const TooltipWhichCountry: React.FC = () => {
  const { t } = useTranslation()
  const topic = t(
    'orderWizardPersonalInformationStepInputLabel_addForeignPersonIdCountry'
  )
  return (
    <Tooltip
      ariaToggleButtonLabelText={t('tooltiplAriaToggle', { topic })}
      ariaCloseButtonLabelText={t('tooltiplAriaClose', { topic })}
    >
      <Text>
        <LocalizedString stringKey="orderWizardPersonalInformationStepIdCountryInfo" />
      </Text>
    </Tooltip>
  )
}
